import type { ButtonHTMLAttributes, DetailedHTMLProps, ForwardedRef, ReactNode } from "react";
import { forwardRef } from "react";
import type { LinkProps } from "../../typography/Link";
import { classNames } from "../../utils/classNames";

type SvgIconProps = {
	className?: string;
	title?: string;
	titleId?: string;
};

type SvgIconComponent = (props: SvgIconProps) => ReactNode;

const linkButtonIntentStyles = {
	primary: "text-primary hover:text-primary-strong",
	secondary: "text-neutral hover:text-neutral-faded",
	tertiary: "text-neutral-faded hover:text-neutral",
};

export type LinkButtonProps = DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> &
	Pick<LinkProps, "size"> & {
		leftIcon?: SvgIconComponent;
		intent?: keyof typeof linkButtonIntentStyles;
	};

const LinkButtonInner = (props: LinkButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
	const { className, size = "md", intent = "primary", leftIcon: LeftIcon, ...rest } = props;

	return (
		<button
			ref={ref}
			{...rest}
			className={classNames(
				"inline-flex items-center justify-center border border-transparent bg-transparent font-medium text-sm underline disabled:cursor-not-allowed disabled:opacity-50",
				linkButtonIntentStyles[intent],
				size === "sm" ? "text-sm" : "text-base",
				className,
			)}
		>
			<>
				{LeftIcon && <LeftIcon className="h-4 min-h-[16px] w-4 min-w-[16px] stroke-2" />}
				{props.children}
			</>
		</button>
	);
};

LinkButtonInner.displayName = "LinkButton";

export const LinkButton = forwardRef(LinkButtonInner);
